import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { Calendar, MapPin, ArrowRight, ExternalLink, FileText, ChevronRight } from 'lucide-react';

const FEATURED_NEWS = [
  {
    id: 1,
    title: "Embaixadora de Angola em Espanha apresenta cartas Figuradas a Ministra dos Negocios Estrangeiros de Andorra",
    source: "A Voz da Diaspora",
    sourceUrl: "https://vozdadiaspora.com/2026/02/embaixadora-de-angola-em-espanha-apresenta-cartas-figuradas-a-ministra-dos-negocios-estrangeiros-de-andorra/",
    date: "19 Fev 2026",
    image: "https://vozdadiaspora.com/wp-content/uploads/2026/02/cartas-andorra-991x564.jpeg"
  },
  {
    id: 2,
    title: "Diplomata realca afirmacao de Africa como espaco de crescimento real",
    source: "ANGOP",
    sourceUrl: "https://www.angop.ao/noticias/economia/diplomata-realca-afirmacao-de-africa-como-espaco-de-crescimento-real/",
    date: "Fev 2026",
    image: "https://customer-assets.emergentagent.com/job_50e85d91-1f1d-48c5-a645-eaeea3c1f4b7/artifacts/mc6fn6ht_Angop.png"
  },
  {
    id: 3,
    title: "Plaza de Toros de Las Ventas acogera el Dia de Angola - 50.o Aniversario",
    source: "El Trapezio",
    sourceUrl: "https://eltrapezio.eu/es/2025/10/25/plaza-de-toros-de-las-ventas-acogera-por-primera-vez-el-dia-de-angola-con-motivo-del-50o-aniversario-de-su-independencia/",
    date: "25 Out 2025",
    image: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/4kxfa3mv_50%20Aniversario_Independencia.jpeg"
  }
];

export default function NewsEvents() {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/events?limit=6').catch(() => ({ data: [] })),
      api.get('/articles?status=published&limit=6').catch(() => ({ data: [] })),
    ]).then(([evtRes, artRes]) => {
      setEvents(evtRes.data || []);
      setArticles(artRes.data?.articles || artRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="news-events-page">
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white border-b-4 border-[#FFD700]">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <p className="text-xs text-[#FFD700] font-semibold uppercase tracking-[0.2em] mb-3">{t('news_events.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('news_events.title')}</h1>
          <p className="text-gray-400 mt-3 max-w-2xl">{t('news_events.subtitle')}</p>
        </div>
      </div>

      {/* Featured External News */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif">{t('news_events.featured_news')}</h2>
              <div className="w-16 h-1 bg-[#CC0000] mt-3" />
            </div>
            <Link to="/noticias" className="flex items-center gap-1 text-sm text-[#CC0000] font-medium hover:underline" data-testid="view-all-news-btn">
              {t('common.view_all')} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURED_NEWS.map(news => (
              <a key={news.id} href={news.sourceUrl} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`featured-news-${news.id}`}>
                <article className="bg-white border border-gray-100 rounded-md overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img src={news.image} alt={news.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <span className="absolute top-2 left-2 px-2 py-1 bg-[#CC0000] text-white text-xs font-medium rounded flex items-center gap-1">
                      <ExternalLink size={10} /> {news.source}
                    </span>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-[#CC0000] transition-colors line-clamp-2">{news.title}</h3>
                    <div className="mt-auto flex items-center justify-between text-xs text-gray-400">
                      <span>{news.date}</span>
                      <span className="text-[#CC0000] font-medium flex items-center gap-1">
                        {t('home.read_article')} <ExternalLink size={10} />
                      </span>
                    </div>
                  </div>
                </article>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Articles */}
      {articles.length > 0 && (
        <section className="py-16 bg-[#F9FAFB]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif">{t('news_events.articles')}</h2>
                <div className="w-16 h-1 bg-[#CC0000] mt-3" />
              </div>
              <Link to="/noticias" className="flex items-center gap-1 text-sm text-[#CC0000] font-medium hover:underline">
                {t('common.view_all')} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 6).map(art => (
                <Link key={art.id} to={`/noticias/${art.id}`} className="group block" data-testid={`article-${art.id}`}>
                  <article className="bg-white border border-gray-100 rounded-md overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all h-full">
                    {art.cover_image && (
                      <div className="h-40 overflow-hidden bg-gray-100">
                        <img src={art.cover_image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 text-sm group-hover:text-[#CC0000] transition-colors line-clamp-2">{art.title}</h3>
                      {art.excerpt && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{art.excerpt}</p>}
                      <div className="flex items-center gap-1 mt-3 text-xs text-[#CC0000] font-medium">
                        {t('common.read_more')} <ChevronRight size={12} />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif">{t('news_events.events_section')}</h2>
              <div className="w-16 h-1 bg-[#CC0000] mt-3" />
            </div>
            <Link to="/eventos" className="flex items-center gap-1 text-sm text-[#CC0000] font-medium hover:underline" data-testid="view-all-events-btn">
              {t('common.view_all')} <ArrowRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-28 bg-gray-100 rounded-md animate-pulse" />)}
            </div>
          ) : events.length === 0 ? (
            <p className="text-gray-400 text-center py-12">{t('events.no_events')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map(evt => (
                <div key={evt.id} className="flex gap-4 p-5 border border-gray-100 rounded-md hover:border-red-100 hover:shadow-sm transition-all" data-testid={`event-${evt.id}`}>
                  <div className="flex-shrink-0 w-14 h-14 bg-[#CC0000] rounded-lg flex flex-col items-center justify-center text-white">
                    <span className="text-xs font-bold">{new Date(evt.start_date).toLocaleString('pt', {month:'short'}).toUpperCase()}</span>
                    <span className="text-lg font-bold leading-none">{new Date(evt.start_date).getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-gray-900 line-clamp-1">{evt.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{evt.description}</p>
                    {evt.location && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                        <MapPin size={11} /> {evt.location}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
